import {useEffect, useState} from "react";
import {FiArrowLeft, FiPlus} from "react-icons/fi";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import { useTranslation } from "react-i18next";
import ActionsMenu from "../../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../../components/common/deleteModal/ConfirmDeleteModal";
import {deleteQuestionByExamBase, getQuestionsByExamBase} from "../../../services/questionsApi";
import AiQuestionGenerateModal from "../aiQuestionGenerateModal/aiQuestionGenerateModal";
import QuestionFormModal from "../questionFormModal/QuestionFormModal";
import TableEmptyState from "../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./ExamBaseQuestionsPage.module.css";
import Loader from "../../../components/common/Loader/Loader";
import QuestionContent from "../../../components/common/QuestionContent/QuestionContent";
import QuestionVideo from "../../../components/common/QuestionVideo/QuestionVideo";
import QuestionExplanation from "../../../components/common/QuestionExplanation/QuestionExplanation";
import SelectFromQuestionBankModal from "../selectFromQuestionBankModal/SelectFromQuestionBankModal";
import {deleteQuestionFromExamBase} from "../../../services/examBasesApi";
import ExamBaseDeleteQuestion
    from "../../../components/common/actionsMenu/examBaseDeleteQuestionModal/ExamBaseDeleteQuestion";
import {getDifficulty} from "../../../utils/difficulty.util";

const ExamBaseQuestionsPage = () => {
    const { t } = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const examBaseId = Number(id);

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    const [isAiModalOpen, setIsAiModalOpen] = useState(false);

    const [isSelectFromQuestionBankModalOpen, setIsSelectFromQuestionBankModalOpen] = useState(false);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState(null);

    const [isDeleteFromExamTemplateOpen, setIsDeleteFromExamTemplateOpen] = useState(false);
    const [questionToDeleteFromExamTemplate, setQuestionToDeleteFromExamTemplate] = useState(null);
    const [deletingQuestionFromExamTemplate, setDeletingQuestionFromExamTemplate] = useState(null);

    const [deleting, setDeleting] = useState(false);
    const [forceDelete, setForceDelete] = useState(false);
    const [forceDeleteMessage, setForceDeleteMessage] = useState("");

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await getQuestionsByExamBase(examBaseId);
            setQuestions(Array.isArray(data) ? data : data.content || []);
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBaseQuestions.loadError");
            setQuestions([]);
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (examBaseId) fetchQuestions();
    }, [examBaseId]);

    const handleCreateClick = () => {
        setEditingQuestion(null);
        setIsModalOpen(true);
    };

    const handleAiCreateClick = () => {
        setIsAiModalOpen(true);
    };

    const handleEditClick = (question) => {
        setEditingQuestion(question);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingQuestion(null);
    };

    const handleCloseAiModal = () => {
        setIsAiModalOpen(false);
    };

    const handleOpenSelectFromQuestionBankModalModal = () => {
        setIsSelectFromQuestionBankModalOpen(true);
    };

    const handleCloseSelectFromQuestionBankModalModal = () => {
        setIsSelectFromQuestionBankModalOpen(false);
    };

    const handleDeleteClick = (question) => {
        setError("");
        setQuestionToDelete(question);
        setIsDeleteOpen(true);
    };

    const handleDeleteFromExamTemplateClick = (question) => {
        setError("");
        setQuestionToDeleteFromExamTemplate(question);
        setIsDeleteFromExamTemplateOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!questionToDelete) return;

        try {
            setDeleting(true);
            setError("");
            await deleteQuestionByExamBase(examBaseId, questionToDelete.id, forceDelete);
            toast.success(t("pages.examBaseQuestions.deleteSuccess"));
            setIsDeleteOpen(false);
            setQuestionToDelete(null);
            setForceDelete(false);
            setForceDeleteMessage("");
            setDeleting(false);
            fetchQuestions();
        } catch (e) {
            if (e.status === 409) {
                setForceDelete(true)
                setForceDeleteMessage(e?.response?.data?.message)
                setDeleting(false);
                return
            }
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBaseQuestions.deleteError");
            setError(msg);
            setIsDeleteOpen(false);
            setQuestionToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setIsDeleteOpen(false);
        setQuestionToDelete(null);
        setForceDelete(false);
        setForceDeleteMessage("");
    };

    const handleConfirmDeleteFromExamTemplateModal = async () => {
        setDeletingQuestionFromExamTemplate(true);
        try {
            setError("");
            await deleteQuestionFromExamBase(examBaseId, questionToDeleteFromExamTemplate.id);
            toast.success(t("pages.examBaseQuestions.removeFromTemplateSuccess"));
            handleCloseDeleteFromExamTemplateModal();
            fetchQuestions();
        } catch (e) {
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.examBaseQuestions.deleteFailed");
            setError(msg);
        }
    }

    const handleCloseDeleteFromExamTemplateModal = () => {
        setIsDeleteFromExamTemplateOpen(false);
        setQuestionToDeleteFromExamTemplate(null);
        setDeletingQuestionFromExamTemplate(false);
    }

    const goBack = () => {
        navigate("/exams/base");
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button type="button" onClick={goBack} className={styles.backBtn}>
                    <FiArrowLeft size={16}/>
                    <span>{t("common.back")}</span>
                </button>

                <div className={styles.questionsBtn}>
                    <button type="button" className={`${styles.btn} ${styles.btnSecondary}`}
                            onClick={handleAiCreateClick}>
                        <img src={process.env.PUBLIC_URL + "/star_emoji.png"} className={styles.icon} alt=""/>
                        <span>{t("pages.examBaseQuestions.aiGenerate")}</span>
                    </button>

                    <button type="button" className={`${styles.btn} ${styles.btnSecondary}`}
                            onClick={handleOpenSelectFromQuestionBankModalModal}>
                        <span className={styles.bookIcon}>📚</span>
                        <span>{t("pages.examBaseQuestions.addFromBank")}</span>
                    </button>

                    <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleCreateClick}>
                        <FiPlus size={18}/>
                        <span>{t("pages.examBaseQuestions.newQuestion")}</span>
                    </button>
                </div>
            </div>

            {loading && <Loader fullscreen/>}

            {error && !loading && <div className={styles.error}>{error}</div>}

            {!loading && questions.length === 0 && !error && (
                <TableEmptyState
                    title={t("pages.examBaseQuestions.noQuestionsTitle")}
                    description={t("pages.examBaseQuestions.noQuestionsDescription")}
                />
            )}

            {!loading &&
                questions.map((q, index) => {
                    const difficulty = getDifficulty(q.difficultyLevel);

                    return (
                        <div key={q.id} className={styles.questionCard}>
                            <div className={styles.questionTopRow}>
                                <div className={styles.questionText}>
                                    <span className={styles.qNumber}>{index + 1}.</span>

                                    <div className={styles.qContent}>
                                        <QuestionContent text={q.text} />
                                    </div>
                                </div>

                                <div className={styles.questionRight}>
                                    <div className={styles.topActions}>
                                      <span
                                          className={`${styles.difficultyBadge} ${
                                              styles[difficulty?.name?.toLowerCase()]
                                          }`}
                                      >
                                      {difficulty?.label || ""}
                                    </span>
                                        <ActionsMenu
                                            onEdit={() => handleEditClick(q)}
                                            onDeleteFromExamTemplate={() => handleDeleteFromExamTemplateClick(q)}
                                            onDelete={() => handleDeleteClick(q)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {q.attachmentUrl && (
                                <img
                                    src={q.attachmentUrl}
                                    alt="question"
                                    className={styles.imagePreview}
                                />
                            )}

                            <QuestionVideo playbackUrl={q.videoPlaybackUrl} thumbnailUrl={q.videoThumbnailUrl}/>

                            <QuestionExplanation explanation={q.explanation}/>

                            <div className={styles.optionsList}>
                                {q.options?.map((opt, idx) => (
                                    <div
                                        key={opt.id || opt.optionText}
                                        className={`${styles.optionItem} ${opt.correct ? styles.correct : ""}`}
                                    >
                                        <span className={styles.optionNumber}>
                                          {String.fromCharCode(65 + idx)})
                                        </span>

                                        <div className={styles.optionContent}>
                                            <QuestionContent text={opt.optionText} />
                                            {opt.attachmentUrl && (
                                                <img
                                                    src={opt.attachmentUrl}
                                                    alt="option"
                                                    className={styles.imagePreview}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}

            <QuestionFormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                examBaseId={examBaseId}
                onSuccess={fetchQuestions}
                mode={editingQuestion ? "edit" : "create"}
                initialQuestion={editingQuestion}
            />

            <AiQuestionGenerateModal
                isOpen={isAiModalOpen}
                onClose={handleCloseAiModal}
                examBaseId={examBaseId}
                onSuccess={fetchQuestions}
            />

            <SelectFromQuestionBankModal
                isOpen={isSelectFromQuestionBankModalOpen}
                onClose={handleCloseSelectFromQuestionBankModalModal}
                examBaseId={examBaseId}
                onSuccess={fetchQuestions}
            />

            <ExamBaseDeleteQuestion
                isOpen={isDeleteFromExamTemplateOpen}
                onConfirm={handleConfirmDeleteFromExamTemplateModal}
                onCancel={handleCloseDeleteFromExamTemplateModal}
                disabled={deletingQuestionFromExamTemplate}
            />

            <ConfirmDeleteModal
                isOpen={isDeleteOpen}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={
                    forceDelete
                        ? forceDeleteMessage
                        : (questionToDelete?.videoUid
                            ? t("pages.examBaseQuestions.deleteConfirmMessageWithVideo", {
                                minutes: Math.ceil((questionToDelete.videoDurationSeconds || 0) / 60),
                            })
                            : t("pages.examBaseQuestions.deleteConfirmMessage"))
                }
                disabled={deleting}
                forceDelete={forceDelete}
            />
        </div>
    );
};

export default ExamBaseQuestionsPage;
